import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import { formatMoney } from '../../../commodity/format/money';
import { LearnerRequestState } from '../api/learner-explore';
import {
  ParentLearnerRequest,
  declineLearnerRequest,
  getParentLearnerRequests,
} from '../api/learner-requests';
import { describeSchedule } from '../domain/catalog';
import { courseZone, formatDayAndTime } from '../time/zonedTime';

const STATE_WORDS: Record<LearnerRequestState, string> = {
  PENDING_PARENT: 'Espera tu decisión',
  PARENT_DECLINED: 'Rechazada',
  PARENT_APPROVED_PENDING_TEACHER: 'Esperando al profesor',
  ENROLLED: 'Inscrito',
  EXPIRED: 'Vencida',
  UNAVAILABLE: 'No disponible',
};

const card: React.CSSProperties = {
  border: '1px solid #dee2e6',
  borderRadius: 10,
  padding: 16,
  background: '#fff',
  marginBottom: 12,
};

/**
 * DEC-S2-S06-06 — what a learner asked for, exactly as they asked.
 *
 * The context is the request's, so a Parent decides on what the child saw. The
 * yes goes to P07's checkout, pinned to this request; there is no approve button
 * here because authority over the enrollment does not live here. A request that
 * stopped being decidable says so and offers the course as it is now, for a new
 * decision: nothing was replaced on the family's behalf.
 */
const PendingLearnerRequests: React.FC = () => {
  const [requests, setRequests] = useState<ParentLearnerRequest[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    setError(null);
    try {
      setRequests((await getParentLearnerRequests()).requests);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const decline = async (request: ParentLearnerRequest) => {
    setNotice(null);
    try {
      await declineLearnerRequest(request.requestId, request.version);
    } catch {
      setNotice('Esta solicitud cambió o no se pudo actualizar. Revísala de nuevo.');
    }
    await fetchRequests();
  };

  if (error) {
    return (
      <ErrorState action="cargar las solicitudes" error={error} onRetry={fetchRequests} compact />
    );
  }
  if (requests === null) return <LoadingState what="las solicitudes" compact />;

  return (
    <section aria-label="Solicitudes de clases" style={{ marginBottom: 24 }}>
      <h3>Solicitudes de clases</h3>
      {requests.length === 0 && (
        <p style={{ color: '#666' }}>Tus hijos no han pedido ninguna clase.</p>
      )}
      {notice && <p role="alert">{notice}</p>}
      {requests.map((request) => (
        <article key={request.requestId} style={card} aria-label={request.courseTitle}>
          <strong>{request.courseTitle}</strong>
          <p style={{ margin: '4px 0' }}>
            {request.learnerFirstName ? `Para ${request.learnerFirstName} · ` : ''}
            {STATE_WORDS[request.state]}
          </p>
          <p style={{ margin: '4px 0', color: '#555', fontSize: 14 }}>
            {request.context.teacherName ? `${request.context.teacherName} · ` : ''}
            {request.context.schedule
              ? `${describeSchedule(request.context.schedule)} (${
                  request.context.schedule.timezone
                })`
              : 'Horario por cita'}
            {request.context.durationWeeks ? ` · ${request.context.durationWeeks} semanas` : ''}
            {request.context.price !== undefined && request.context.currency
              ? ` · ${formatMoney(request.context.price, request.context.currency)} por sesión`
              : ''}
          </p>
          {request.context.session && (
            <p style={{ margin: '4px 0', color: '#555', fontSize: 14 }}>
              Clase pedida:{' '}
              {formatDayAndTime(
                request.context.session.startsAt,
                courseZone(request.context.session.timezone),
              )}{' '}
              ({request.context.session.timezone})
            </p>
          )}
          {request.decidable ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Link
                to={`/courses/${encodeURIComponent(
                  request.courseId,
                )}/enroll?request=${encodeURIComponent(request.requestId)}`}
              >
                Revisar y continuar
              </Link>
              <button type="button" onClick={() => decline(request)}>
                Rechazar
              </button>
            </div>
          ) : (
            (request.state === 'EXPIRED' || request.state === 'UNAVAILABLE') && (
              <p style={{ margin: '4px 0', fontSize: 14 }}>
                Lo que se pidió ya no está disponible así y no lo cambiamos por otro horario.{' '}
                <Link to={`/courses/${encodeURIComponent(request.courseId)}`}>
                  Ver el curso como está ahora
                </Link>
              </p>
            )
          )}
        </article>
      ))}
    </section>
  );
};

export default PendingLearnerRequests;
